import { useEffect, useRef, useState } from 'react';
import { axios } from '../App';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, CheckCircle, Crosshair, Power, Radio, Shield,
  ShieldAlert, Target, Wifi, WifiOff, Cpu, Zap
} from 'lucide-react';

const SOFT_KILL_INFO = {
  rf_jamming: { label: 'RF Jamming', icon: Radio, color: 'text-yellow-400' },
  gps_spoofing: { label: 'GPS Spoofing', icon: Wifi, color: 'text-blue-400' },
  comm_disruption: { label: 'Comm Disruption', icon: WifiOff, color: 'text-orange-400' },
  cyber_takeover: { label: 'Cyber Takeover', icon: Cpu, color: 'text-purple-400' },
};

const HARD_KILL_INFO = {
  directed_energy: { label: 'Directed Energy Weapon (DEW)', color: 'text-red-500', desc: 'High-energy laser to disable/destroy' },
  anti_drone_gun: { label: 'Anti Drone Gun', color: 'text-amber-500', desc: 'Rapid-fire kinetic projectile / autocannon' },
  kinetic_interceptor: { label: 'Kinetic Interceptor', color: 'text-orange-500', desc: 'Physical projectile intercept' },
  net_capture: { label: 'Net Capture', color: 'text-green-500', desc: 'Drone capture net deployment' },
  anti_drone_missile: { label: 'Anti-Drone Missile', color: 'text-red-600', desc: 'Guided missile intercept' },
};

const STAGE_LABEL = {
  detected: 'Detected',
  analyzing: 'Analyzing',
  soft_kill_deployed: 'Soft Kill Deployed',
  soft_kill_success: 'Neutralized (Soft Kill)',
  awaiting_hard_kill: 'Awaiting Operator',
  hard_kill_authorized: 'Hard Kill Authorized',
  destroyed: 'Destroyed',
  error: 'Error',
};

const STAGE_COLOR = {
  detected: 'text-zinc-300 bg-zinc-800 border-zinc-700',
  analyzing: 'text-sky-400 bg-sky-500/10 border-sky-500/40',
  soft_kill_deployed: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/40',
  soft_kill_success: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/40',
  awaiting_hard_kill: 'text-red-400 bg-red-500/10 border-red-500/50 animate-pulse',
  hard_kill_authorized: 'text-orange-400 bg-orange-500/10 border-orange-500/40',
  destroyed: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/40',
  error: 'text-red-500 bg-red-500/10 border-red-500/40',
};

export default function AutoEngage() {
  const [engaged, setEngaged] = useState(false);
  const [engagements, setEngagements] = useState([]); // { drone_id, ...stage data }
  const [hardKillPrompt, setHardKillPrompt] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ detected: 0, soft_killed: 0, hard_killed: 0, breached: 0 });
  const [liveDemo, setLiveDemo] = useState(() => {
    try { return localStorage.getItem('bk_live_demo') === '1'; } catch { return false; }
  });

  const engagedRef = useRef(false);
  const engagementsRef = useRef({});
  const statsRef = useRef({ detected: 0, soft_killed: 0, hard_killed: 0, breached: 0 });
  const logsRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => { engagedRef.current = engaged; }, [engaged]);

  // Auto-arm engagement when Live Demo is on (and persist flag for cross-page sync)
  useEffect(() => {
    try {
      if (liveDemo) localStorage.setItem('bk_live_demo', '1');
      else localStorage.removeItem('bk_live_demo');
    } catch { /* ignore */ }
    if (liveDemo && !engagedRef.current) {
      engagedRef.current = true;
      setEngaged(true);
    }
  }, [liveDemo]);

  const pushLog = (message, type = 'info') => {
    const entry = { ts: new Date().toLocaleTimeString(), message, type };
    logsRef.current = [entry, ...logsRef.current].slice(0, 80);
    setLogs([...logsRef.current]);
  };

  const bumpStat = (field) => {
    statsRef.current = { ...statsRef.current, [field]: statsRef.current[field] + 1 };
    setStats({ ...statsRef.current });
  };

  const upsertEngagement = (drone_id, patch) => {
    const prev = engagementsRef.current[drone_id] || { drone_id, started_at: Date.now() };
    const next = { ...prev, ...patch, updated_at: Date.now() };
    engagementsRef.current[drone_id] = next;
    setEngagements(Object.values(engagementsRef.current).sort((a, b) => b.started_at - a.started_at));
  };

  const startEngagement = async (drone) => {
    const drone_id = drone.drone_id;
    if (engagementsRef.current[drone_id]) return; // already engaged

    upsertEngagement(drone_id, {
      drone_type: drone.drone_type, domain: drone.domain,
      threat_level: drone.threat_level, stage: 'detected',
    });
    bumpStat('detected');
    pushLog(`[CONTACT] ${drone_id} ${drone.drone_type || 'unknown'} (${drone.threat_level}) detected — auto-analyzing`, 'warning');

    upsertEngagement(drone_id, { stage: 'analyzing' });
    let analysis = null;
    try {
      const res = await axios.post('/mitigation/analyze', {
        source: 'auto_engage',
        drone_id,
        drone_type: drone.drone_type || 'unknown',
        threat_level: drone.threat_level || 'high',
        domain: drone.domain || 'air',
        speed: String(drone.speed ?? ''),
        altitude: String(drone.altitude ?? ''),
        distance: '',
        heading: String(drone.heading ?? ''),
        payload_type: '',
        additional_info: 'Auto-engagement pipeline'
      });
      analysis = res.data;
    } catch (err) {
      pushLog(`[ERROR] ${drone_id} analyzer failed: ${err.response?.data?.detail || err.message}`, 'danger');
      upsertEngagement(drone_id, { stage: 'error' });
      return;
    }

    const sk = analysis.soft_kill || {};
    const skLabel = SOFT_KILL_INFO[sk.recommended]?.label || sk.recommended || 'Unknown';
    upsertEngagement(drone_id, { analysis, urgency: analysis.urgency, soft_kill: sk });
    pushLog(`[ANALYZED] ${drone_id} urgency ${analysis.urgency?.toUpperCase()} — soft kill: ${skLabel} (${sk.confidence ?? '?'}% confidence)`, 'info');

    // Auto-deploy soft kill
    try {
      await axios.post(`/mitigation/deploy-soft-kill?decision_id=${analysis.decision_id}`);
      upsertEngagement(drone_id, { stage: 'soft_kill_deployed' });
      pushLog(`[SOFT KILL] ${drone_id} ${skLabel} deployed`, 'warning');
    } catch (err) {
      pushLog(`[ERROR] ${drone_id} soft kill deploy failed`, 'danger');
      upsertEngagement(drone_id, { stage: 'error' });
      return;
    }

    // Resolve outcome from estimated_effectiveness probability
    const eff = (sk.estimated_effectiveness ?? 60) / 100;
    const success = Math.random() < eff;

    setTimeout(() => {
      if (success) {
        upsertEngagement(drone_id, { stage: 'soft_kill_success' });
        bumpStat('soft_killed');
        pushLog(`[NEUTRALIZED] ${drone_id} soft kill SUCCESS — target neutralized by ${skLabel}`, 'success');
      } else {
        upsertEngagement(drone_id, { stage: 'awaiting_hard_kill', soft_kill_failed: true });
        pushLog(`[ALERT] ${drone_id} soft kill FAILED — operator authorization required`, 'danger');
        // Surface the hard-kill prompt — most recent failure wins focus
        setHardKillPrompt({
          drone_id,
          drone_type: drone.drone_type,
          threat_level: drone.threat_level,
          domain: drone.domain,
          urgency: analysis.urgency,
          decision_id: analysis.decision_id,
          hard_kill_options: (analysis.hard_kill_options || []).slice().sort((a, b) => (a.rank || 99) - (b.rank || 99)),
        });
      }
    }, 1200);
  };

  const handleHardKill = async (action_type) => {
    if (!hardKillPrompt) return;
    const { drone_id, decision_id } = hardKillPrompt;
    const label = HARD_KILL_INFO[action_type]?.label || action_type;
    try {
      await axios.post(`/mitigation/authorize-hard-kill?decision_id=${decision_id}&action_type=${action_type}`);
      upsertEngagement(drone_id, { stage: 'hard_kill_authorized', hard_kill: action_type });
      pushLog(`[HARD KILL] ${drone_id} authorized: ${label}`, 'danger');
      // Visual destruction confirmation
      setTimeout(() => {
        upsertEngagement(drone_id, { stage: 'destroyed' });
        bumpStat('hard_killed');
        pushLog(`[DESTROYED] ${drone_id} target destroyed by ${label}`, 'success');
      }, 1500);
    } catch (err) {
      pushLog(`[ERROR] ${drone_id} hard kill authorization failed`, 'danger');
    } finally {
      setHardKillPrompt(null);
    }
  };

  const dismissHardKill = () => {
    if (!hardKillPrompt) return;
    pushLog(`[OPERATOR] ${hardKillPrompt.drone_id} hard kill declined — manual review required`, 'warning');
    upsertEngagement(hardKillPrompt.drone_id, { stage: 'error' });
    setHardKillPrompt(null);
  };

  // Polling loop
  useEffect(() => {
    const tick = async () => {
      if (!engagedRef.current) return;
      try {
        const res = await axios.get('/drones');
        const active = res.data.filter(d => d.status === 'active');
        for (const drone of active) {
          if (!engagementsRef.current[drone.drone_id]) {
            // Start asynchronously, don't block loop
            startEngagement(drone);
          }
        }
      } catch {
        // silent
      }
    };
    timerRef.current = setInterval(tick, 4000);
    tick();
    return () => clearInterval(timerRef.current);
  }, []);

  const toggleEngaged = () => {
    setEngaged(e => {
      const next = !e;
      engagedRef.current = next;
      pushLog(next ? '[SYSTEM] AUTO ENGAGE ARMED — pipeline active' : '[SYSTEM] AUTO ENGAGE DISARMED', next ? 'success' : 'warning');
      if (!next) setHardKillPrompt(null);
      return next;
    });
  };

  const resetAll = () => {
    engagementsRef.current = {};
    setEngagements([]);
    statsRef.current = { detected: 0, soft_killed: 0, hard_killed: 0, breached: 0 };
    setStats({ ...statsRef.current });
    logsRef.current = [];
    setLogs([]);
    setHardKillPrompt(null);
    pushLog('[SYSTEM] Engagement log cleared', 'info');
  };

  return (
    <div className="h-full flex flex-col bg-zinc-950 overflow-hidden" data-testid="auto-engage-page">
      {/* Header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800 p-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
            <Activity size={22} className="text-sky-500" />
            Auto Engage
          </h1>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
            Detect → Analyze → Auto Soft-Kill → Operator Hard-Kill Authorization
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setLiveDemo(v => !v)}
            className={`px-5 py-2 rounded font-mono text-xs uppercase tracking-wider border-2 transition-all flex items-center gap-2 ${
              liveDemo
                ? 'bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400 shadow-[0_0_22px_rgba(217,70,239,0.5)]'
                : 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:border-fuchsia-500 hover:text-fuchsia-300'
            }`}
            data-testid="live-demo-master-ae"
            title="One-click: arms Live Mode Demo Feed + Auto Hand-off + this engagement loop"
          >
            <Cpu size={14} />
            {liveDemo ? '🟣 LIVE DEMO: ARMED' : 'LIVE DEMO'}
          </button>
          <button
            onClick={resetAll}
            className="px-3 py-2 rounded font-mono text-xs uppercase bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600 transition-colors"
            data-testid="auto-reset"
          >
            Reset
          </button>
          <button
            onClick={toggleEngaged}
            className={`px-4 py-2 rounded font-mono text-xs uppercase tracking-wider border-2 transition-all ${
              engaged
                ? 'bg-red-500/20 text-red-300 border-red-500 shadow-[0_0_18px_rgba(239,68,68,0.35)]'
                : 'bg-emerald-500/10 text-emerald-300 border-emerald-600 hover:bg-emerald-500/20'
            }`}
            data-testid="auto-engage-toggle"
          >
            <span className="flex items-center gap-2">
              <Power size={14} />
              {engaged ? 'AUTO ENGAGE: ARMED' : 'AUTO ENGAGE: STANDBY'}
            </span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 p-4 border-b border-zinc-800">
        {[
          { label: 'Detected', val: stats.detected, color: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10' },
          { label: 'Soft-Killed', val: stats.soft_killed, color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
          { label: 'Hard-Killed', val: stats.hard_killed, color: 'text-red-400 border-red-500/30 bg-red-500/10' },
          { label: 'Active', val: engagements.filter(e => !['soft_kill_success','destroyed','error'].includes(e.stage)).length, color: 'text-sky-400 border-sky-500/30 bg-sky-500/10' },
        ].map(s => (
          <div key={s.label} className={`px-4 py-3 rounded border ${s.color}`} data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className="text-2xl font-bold font-mono">{s.val}</div>
            <div className="text-[10px] uppercase tracking-wider mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Body: engagements grid + log */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 p-4 overflow-y-auto" data-testid="engagement-list">
          <h2 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
            <Target size={16} className="text-red-500" /> Live Engagements
            <span className="text-xs text-zinc-600 font-mono ml-1">{engagements.length}</span>
          </h2>
          {engagements.length === 0 ? (
            <div className="text-zinc-600 font-mono text-xs py-12 text-center">
              {engaged ? 'Awaiting contacts...' : 'Press AUTO ENGAGE to arm pipeline.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {engagements.map(e => {
                const stageStyle = STAGE_COLOR[e.stage] || STAGE_COLOR.detected;
                const skInfo = SOFT_KILL_INFO[e.soft_kill?.recommended];
                const SkIcon = skInfo?.icon || Zap;
                return (
                  <div
                    key={e.drone_id}
                    className="bg-zinc-950/80 border border-zinc-800 rounded-sm p-3"
                    data-testid={`engagement-${e.drone_id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="text-[10px] font-mono uppercase text-zinc-500">{e.drone_id}</div>
                        <div className="text-sm font-bold uppercase">{e.drone_type || 'Unknown'}</div>
                        <div className="text-[10px] font-mono text-zinc-500 uppercase">{e.domain} · {e.threat_level}</div>
                      </div>
                      <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded border ${stageStyle}`}>
                        {STAGE_LABEL[e.stage] || e.stage}
                      </span>
                    </div>
                    {e.urgency && (
                      <div className="text-[11px] font-mono text-zinc-400 mb-1.5">
                        Urgency: <span className="uppercase font-bold">{e.urgency}</span>
                      </div>
                    )}
                    {e.soft_kill?.recommended && (
                      <div className="flex items-center gap-1.5 text-[11px] mb-1">
                        <SkIcon size={12} className={skInfo?.color || 'text-zinc-400'} />
                        <span className={skInfo?.color}>{skInfo?.label || e.soft_kill.recommended}</span>
                        <span className="text-zinc-600 font-mono ml-auto">{e.soft_kill.estimated_effectiveness}%</span>
                      </div>
                    )}
                    {e.hard_kill && (
                      <div className="text-[11px] mt-1.5 pt-1.5 border-t border-zinc-800">
                        <span className="text-red-400 uppercase font-bold">Hard Kill:</span>{' '}
                        <span className="text-zinc-300">{HARD_KILL_INFO[e.hard_kill]?.label || e.hard_kill}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Event log */}
        <div className="w-96 border-l border-zinc-800 flex flex-col">
          <div className="p-3 border-b border-zinc-800 flex items-center gap-2">
            <Shield size={14} className="text-sky-500" />
            <span className="text-xs font-bold uppercase tracking-wider">Event Log</span>
            <span className="ml-auto text-[10px] text-zinc-600 font-mono">{logs.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-0.5 font-mono text-[11px] bg-black/30" data-testid="event-log">
            {logs.length === 0 ? (
              <p className="text-zinc-500">Awaiting events...</p>
            ) : (
              logs.map((l, i) => {
                const c = { warning: 'text-yellow-400', danger: 'text-red-400', success: 'text-emerald-400' }[l.type] || 'text-zinc-400';
                return (
                  <div key={i} className={c}>
                    <span className="text-zinc-600">[{l.ts}]</span> {l.message}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Hard-kill modal */}
      {hardKillPrompt && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-30" data-testid="auto-hard-kill-modal">
          <div className="bg-zinc-900 border-2 border-red-500 rounded-lg p-6 max-w-2xl w-full mx-4">
            <div className="flex items-center gap-2 text-red-500 mb-4">
              <AlertTriangle size={24} />
              <h3 className="text-lg font-bold uppercase">Hard Kill Authorization Required</h3>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded p-3 mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div><span className="text-zinc-500">Target:</span> <span className="text-white font-mono">{hardKillPrompt.drone_id}</span></div>
                <div><span className="text-zinc-500">Type:</span> <span className="text-white">{hardKillPrompt.drone_type || 'Unknown'}</span></div>
                <div><span className="text-zinc-500">Threat:</span> <span className="text-red-400 font-bold uppercase">{hardKillPrompt.threat_level}</span></div>
                <div><span className="text-zinc-500">Urgency:</span> <span className="text-red-400 font-bold uppercase">{hardKillPrompt.urgency}</span></div>
              </div>
              <p className="text-xs text-red-400 mt-2">Soft kill failed. Recommended hard-kill options ranked by AI:</p>
            </div>

            <div className="space-y-2 mb-4 max-h-[55vh] overflow-y-auto">
              {hardKillPrompt.hard_kill_options.map((opt, i) => {
                const info = HARD_KILL_INFO[opt.type] || { label: opt.type, desc: '' };
                const isTop = i === 0;
                return (
                  <button
                    key={opt.type}
                    onClick={() => handleHardKill(opt.type)}
                    className={`w-full text-left p-3 rounded border transition-all ${
                      isTop
                        ? 'border-emerald-500 bg-emerald-500/5 hover:bg-emerald-500/15'
                        : 'border-zinc-700 bg-zinc-800/50 hover:border-red-500 hover:bg-red-500/10'
                    }`}
                    data-testid={`auto-hardkill-${opt.type}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {isTop && (
                            <span className="text-[9px] font-bold font-mono uppercase px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                              AI Recommended
                            </span>
                          )}
                          <span className={`text-sm font-bold ${info.color || 'text-zinc-200'}`}>{info.label}</span>
                          <span className="text-[10px] font-mono text-zinc-500">#{opt.rank}</span>
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">{info.desc}</div>
                        {opt.reasoning && (
                          <div className="text-[11px] text-zinc-400 mt-1 italic">{opt.reasoning}</div>
                        )}
                      </div>
                      <div className="text-right text-[10px] font-mono">
                        <div className="text-emerald-400">{opt.estimated_effectiveness}%</div>
                        <div className="text-zinc-500 uppercase">{opt.collateral_risk} coll.</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <button
              onClick={dismissHardKill}
              className="w-full py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-xs font-mono uppercase text-zinc-400 transition-colors"
              data-testid="auto-decline-hardkill"
            >
              Decline — Hold Fire
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
